import React, { useState, useEffect, useMemo } from "react";
import {
    TextField,
    Autocomplete,
} from "@mui/material";
import { useSelector } from "react-redux";

const LocalAutocomplete = React.forwardRef(({ readOnly, placeholder, labelField, valueField = 'id', name, onChange, value, error, helperText, list, required, showLabel = true, ref, vehicleMakeId }) => {

    const [inputValue, setInputValue] = useState("");
    const [validationError, setValidationError] = useState("");

    const [options, setOptions] = useState([]);
    let items;

    const getLabel = (option) => {
        if (!option) return '';
        if (typeof option === 'string') return option;
        if (Array.isArray(labelField)) {
            return labelField.map((key) => option?.[key] ?? '').join(' - ');
        }
        return option?.[labelField] ?? '';
    };

    const handleChange = (_, newValue) => {
        const selectedValue = newValue?.[valueField] ?? newValue;
        onChange?.(selectedValue);
        setValidationError('');
        setInputValue(newValue ? getLabel(newValue) : '')
    }

    const handleInputChange = (_, newInputValue, reason) => {
        setInputValue(newInputValue);
        // if (reason === 'input') {
        //     const matchingOption = options.find(opt => getLabel(opt).toLowerCase() === newInputValue.toLowerCase());
        //     if (!matchingOption) {
        //         setValidationError('Please select a valid value from the list');
        //         onChange?.(null);
        //     } else {
        //         setValidationError('');
        //         onChange?.(matchingOption[valueField]);
        //     }
        // }
        if (reason === 'reset') {
            setValidationError('');
        }
    }

    const handleBlur = (e) => {
        const matchingOption = options.find(opt => getLabel(opt).toLowerCase() ===  e.target.value.toLowerCase());
        if (!matchingOption) {
            if(!readOnly) {
                setValidationError('Please select a valid value from the list');
            }
            onChange?.(null);
        } else {
            setValidationError('');
            onChange?.(matchingOption[valueField]);
        }
    }

    const allTasks = useSelector((state) => state.tasks.allTasks);
    const allPatrols = useSelector((state) => state.patrols.allPatrols);
    const vehicleData = useSelector(state => state.constants.data);
    const taskStatuses = useSelector((state) => state.constants.data.taskStatuses);
    const completedStatusId = useMemo(() => {
        return taskStatuses?.find(t => t.code === 'COMP')?.id;
    }, [taskStatuses]);
    const filteredTasks = useMemo(() => {
        if (!completedStatusId) return allTasks;
        return allTasks.filter(t => t.statusId != completedStatusId);
    }, [allTasks, completedStatusId]);

    items = useMemo(() => {
        if (list === 'vehicleMakes' || list === 'vehicleModels') {
            let listItems = vehicleData?.[list] ?? [];
            if (vehicleMakeId) {
                listItems = listItems.filter((i) => i.makeId === vehicleMakeId);
            }
            return listItems;
        }

        if (list === 'tasks') {
            return filteredTasks;
        }

        if (list === 'patrols') {
            return allPatrols;
        }

        return [];
    }, [list, vehicleData, vehicleMakeId, filteredTasks, allPatrols]);

    useEffect(() => {
        if (!value || !items?.length) return;
        const selected = items.find(item => item[valueField] == value);
        if (!selected) return;
        if (selected) {
            setOptions((prev) => {
                const alreadyIncluded = prev.some(item => item[valueField] === value);
                return alreadyIncluded ? prev : [selected, ...prev];
            });
        }
    }, [value, items]);

    useEffect(() => {
        if (!items?.length) return;
        const filtered = items.filter((i) => {
            if (list === 'vehicleModels' && vehicleMakeId) {
                if (i.makeId !== vehicleMakeId) return false;
            }
            if (!inputValue) return true;
            return getLabel(i).toLowerCase().includes(inputValue.toLowerCase());
        });
        setOptions(filtered);
    }, [inputValue, items, list, vehicleMakeId]);


    const selectedOption = value ? options.find(opt => opt[valueField] == value) || null : null;

    useEffect(() => {
        setOptions(items || [])
    }, [items, selectedOption]);

    return (
        <Autocomplete
            key={value ?? 'null'}
            freeSolo
            readOnly={readOnly}
            sx={{ pt: 1, mt: 1 }}
            options={options || []}
            filterOptions={(x) => x}
            autoComplete
            includeInputInList
            value={selectedOption ?? null}
            noOptionsText="No options"
            onInputChange={handleInputChange}
            onBlur={handleBlur}
            renderOption={(props, option) => (
                <li {...props} key={option[valueField]}>{getLabel(option)}</li>
            )}
            isOptionEqualToValue={(option, value) => option?.[valueField] === value?.[valueField]}
            onChange={handleChange}
            getOptionLabel={getLabel}
            renderInput={(params) =>
                <TextField
                    {...params}
                    required={typeof required === 'boolean' ? required : !!error && helperText?.toLowerCase().includes('required')}
                    label={showLabel ? placeholder : undefined}
                    placeholder={!showLabel ? placeholder : undefined}
                    inputRef={ref}
                    name={name}
                    sx={{ '& .MuiInputLabel-asterisk': { color: 'red' } }}
                    error={!!error || !!validationError}
                    helperText={validationError || helperText}
                />
            }
        />
    );
});

export default LocalAutocomplete;